/**
 * WhatIfSimulator, interaktivní simulátor dopadů na valuaci (PRO feature).
 * Uživatel nastaví parametry, debounced API call vrátí delta valuace.
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { computeWhatIf } from '../api/whatIfApi';
import { buildValuationRequest } from '../api/valuationCalculator';

function fmt(v) {
  if (v === null || v === undefined) return '-';
  return Math.round(v).toLocaleString('cs-CZ');
}

export function WhatIfSimulator({ wizardState, baseValuation }) {
  const [adjustments, setAdjustments] = useState({
    marketing_invest: 0,
    opex_reduction_pct: 0,
    price_increase_pct: 0,
    rrs_improvement: 0,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  const updateAdj = (key, value) => {
    setAdjustments(prev => ({ ...prev, [key]: Math.max(0, Number(value) || 0) }));
  };

  const hasAnyAdjustment = Object.values(adjustments).some(v => v > 0);

  // Debounced API call
  const runSimulation = useCallback(async () => {
    if (!hasAnyAdjustment) {
      setResult(null);
      return;
    }
    setLoading(true);
    try {
      const baseRequest = buildValuationRequest(wizardState);
      const res = await computeWhatIf(baseRequest, adjustments, wizardState.savedValuationId || null);
      if (res.success) {
        setResult(res);
      }
    } catch (e) {
      console.error('What-if error:', e);
    } finally {
      setLoading(false);
    }
  }, [adjustments, wizardState, hasAnyAdjustment]);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(runSimulation, 800);
    return () => clearTimeout(timerRef.current);
  }, [runSimulation]);

  const v = baseValuation;
  const delta = result ? result.delta : 0;
  const deltaPct = result ? result.delta_pct : 0;

  return (
    <div style={{ border: '2px solid var(--purple-bright)', borderRadius: 14, overflow: 'hidden', marginBottom: '1.5rem' }}>
      <div style={{ background: 'var(--btn-bg)', padding: '1rem 1.25rem', color: 'white' }}>
        <div style={{ fontSize: '1rem', fontWeight: 700 }}>What-If Simulátor</div>
        <div style={{ fontSize: '0.82rem', opacity: 0.85 }}>
          Upravte parametry a sledujte dopad na valuaci v reálném čase
        </div>
      </div>

      <div style={{ padding: '1.25rem', background: 'var(--purple-deep)' }}>
        {/* Sliders */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
          {/* Marketing investice */}
          <div>
            <label style={{ fontSize: '0.82rem', color: 'var(--text-dim)', display: 'block', marginBottom: '0.3rem' }}>
              Investice do marketingu (Kč/rok)
            </label>
            <input
              type="number"
              value={adjustments.marketing_invest || ''}
              onChange={e => updateAdj('marketing_invest', e.target.value)}
              placeholder="např. 500 000"
              min="0"
              style={{ width: '100%' }}
            />
            <small style={{ color: 'var(--text-dim)', fontSize: '0.75rem' }}>
              Předpokládaný ROAS: 3× (konzervativní odhad)
            </small>
          </div>

          {/* Snížení OPEX */}
          <div>
            <label style={{ fontSize: '0.82rem', color: 'var(--text-dim)', display: 'block', marginBottom: '0.3rem' }}>
              Snížení provozních nákladů (%)
            </label>
            <input
              type="number"
              value={adjustments.opex_reduction_pct || ''}
              onChange={e => updateAdj('opex_reduction_pct', e.target.value)}
              placeholder="např. 10"
              min="0" max="50"
              style={{ width: '100%' }}
            />
            <small style={{ color: 'var(--text-dim)', fontSize: '0.75rem' }}>
              Optimalizace procesů, automatizace, vyjednání lepších podmínek
            </small>
          </div>

          {/* Zvýšení cen */}
          <div>
            <label style={{ fontSize: '0.82rem', color: 'var(--text-dim)', display: 'block', marginBottom: '0.3rem' }}>
              Zvýšení cen (%)
            </label>
            <input
              type="number"
              value={adjustments.price_increase_pct || ''}
              onChange={e => updateAdj('price_increase_pct', e.target.value)}
              placeholder="např. 5"
              min="0" max="30"
              style={{ width: '100%' }}
            />
            <small style={{ color: 'var(--text-dim)', fontSize: '0.75rem' }}>
              Zahrnuje elasticitu poptávky (-2 % objemu / +1 % ceny)
            </small>
          </div>

          {/* Zlepšení RRS */}
          <div>
            <label style={{ fontSize: '0.82rem', color: 'var(--text-dim)', display: 'block', marginBottom: '0.3rem' }}>
              Zlepšení retence (p.b.)
            </label>
            <input
              type="number"
              value={adjustments.rrs_improvement || ''}
              onChange={e => updateAdj('rrs_improvement', e.target.value)}
              placeholder="např. 10"
              min="0" max="30"
              style={{ width: '100%' }}
            />
            <small style={{ color: 'var(--text-dim)', fontSize: '0.75rem' }}>
              Loajalitní programy, e-mail marketing, lepší UX
            </small>
          </div>
        </div>

        {/* Výsledek */}
        {hasAnyAdjustment && (
          <div style={{
            background: delta > 0 ? 'rgba(5, 150, 105, 0.08)' : delta < 0 ? 'rgba(220, 38, 38, 0.08)' : 'rgba(107, 63, 160, 0.06)',
            border: `1px solid ${delta > 0 ? 'rgba(5, 150, 105, 0.25)' : delta < 0 ? 'rgba(220, 38, 38, 0.25)' : 'rgba(155, 89, 212, 0.15)'}`,
            borderRadius: 10, padding: '1rem',
          }}>
            {loading ? (
              <div style={{ textAlign: 'center', color: 'var(--text-dim)', fontSize: '0.85rem' }}>
                Počítám...
              </div>
            ) : result ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '1rem', alignItems: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>Současná valuace</div>
                  <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--star-white)' }}>{fmt(result.base_valuation)} Kč</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', color: delta > 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>→</div>
                  <div style={{ fontSize: '0.8rem', fontWeight: 700, color: delta > 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>
                    {delta >= 0 ? '+' : ''}{fmt(delta)} Kč
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>
                    ({deltaPct >= 0 ? '+' : ''}{deltaPct} %)
                  </div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>Nová valuace</div>
                  <div style={{ fontWeight: 700, fontSize: '1rem', color: delta > 0 ? 'var(--color-success)' : 'var(--star-white)' }}>{fmt(result.new_valuation)} Kč</div>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', color: 'var(--text-dim)', fontSize: '0.85rem' }}>
                Zadejte alespoň jeden parametr
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
